import type { FilteringService } from '.';

import { Environment } from '../../../environments/environment';

export interface SearchApiResult {
  fields: string[];
  highlight: Record<string, SearchHighlightToken | SearchRepHighlight[] | undefined>;
  id: string;
}

export interface SearchHighlightToken {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- This matches the API's field name.
  matched_tokens?: string[];
}

export interface SearchRepHighlight {
  firstName?: SearchHighlightToken;
  lastName?: SearchHighlightToken;
}

export async function SetSearch(this: FilteringService, term: string): Promise<void> {
  if (!term) {
    return;
  }

  // this.searchModel.term = term;
  // this.search.set(term);

  const _results = await searchFetch.call(this, term);

  if (typeof _results[0] === 'string') {
    this.ids.set(_results as string[]);
    return;
  }

  const _searchResults = _results as SearchApiResult[];

  const _searchModelIds: string[] = [];
  for (const _result of _searchResults) {
    if (!_result.fields.includes('modelTitle')) {
      continue;
    }

    const _modelId = this.stateService.allListingIds().find((l) => l.id === _result.id)?.modelId;
    if (!_modelId) {
      continue;
    }

    if (!this.searchModels().includes(_modelId)) {
      this.searchModels.update((models) => [...models, _modelId]);
      _searchModelIds.push(_modelId);
    }
  }

  const _relatedModelIds: string[] = [];
  for (const _modelId of _searchModelIds) {
    const _model = this.availableModels.value()?.find((m) => m.id === _modelId);
    if (!_model?.relatedModels) {
      continue;
    }

    for (const _relatedId of _model.relatedModels) {
      if (_relatedModelIds.includes(_relatedId)) {
        continue;
      }

      _relatedModelIds.push(_relatedId);
    }
  }

  this.relatedSearchModelIds.set(_relatedModelIds);

  // this.models.set([]);
  // return this.ids.set(_results.map(r => r.id));
}

async function searchFetch(
  this: FilteringService,
  term: string,
): Promise<(SearchApiResult | string)[]> {
  const _url = `https://api.v2.thevillages.com/homes/listings/search?q=${term}`;
  const _authorization = this.stateService.userToken()
    ? `Bearer ${this.stateService.userToken()}`
    : Environment.basicToken;
  const _options: RequestInit = {
    headers: {
      Authorization: _authorization,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- This is the correct header name for content type.
      'Content-Type': 'application/json',
    },
  };

  const _response: unknown = await fetch(_url, _options).then((r) => (r.ok ? r.json() : []));

  if (!Array.isArray(_response)) {
    return [];
  }

  const _results = _response as (SearchApiResult | string)[];

  const _searchOptionProps = Array.from(
    new Set(_results.flatMap((r) => (typeof r === 'string' ? [] : r.fields))),
  );

  const _searchOptions = _searchOptionProps.map((key) => {
    return {
      ids: [
        ...new Set(
          _results.map((r) => {
            if (typeof r !== 'string' && r.fields.includes(key)) {
              return r.id;
            }
            return undefined;
          }),
        ),
      ],
      key,

      matchedTokens: [
        ...new Set(
          _results.flatMap((r) => {
            if (typeof r === 'string' || !r.fields.includes(key)) {
              return [];
            }

            const _keyHighlight = r.highlight[key];
            if (!_keyHighlight) {
              return [];
            }

            if (key === 'listingReps' && Array.isArray(_keyHighlight)) {
              return _keyHighlight.flatMap((rep) => {
                if (rep.firstName?.matched_tokens?.length) {
                  return rep.firstName.matched_tokens;
                }
                if (rep.lastName?.matched_tokens?.length) {
                  return rep.lastName.matched_tokens;
                }
                return [];
              });
            }

            if (!Array.isArray(_keyHighlight)) {
              return _keyHighlight.matched_tokens ?? [];
            }

            return [];
          }),
        ),
      ],
    };
  });

  this.searchOptionProps.set(_searchOptions);

  return _results;
}
